import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Lock, LogIn, AlertCircle, X, ArrowRight, Search, Layers, Rocket, Compass, Workflow } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ============ CUSTOM COLORS (matching riicu.ai Tailwind config) ============
const colors = {
  ink: '#0a0f1a',
  accentGlow: '#d4a853',
  accentBrand: '#2563eb',
  surface: '#f8f7f4',
};

// ============ DATA RIVERS BACKGROUND (exact match) ============
const DataRiversBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let nodes = [];
    let concentricPoints = [];
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      createElements();
    };
    
    const createElements = () => {
      nodes = [];
      concentricPoints = [];
      
      // Create flowing nodes
      const numNodes = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          radius: Math.random() * 1.8 + 0.5,
          type: Math.random() > 0.92 ? 'concentric' : Math.random() > 0.85 ? 'large' : Math.random() > 0.7 ? 'medium' : 'small',
          pulsePhase: Math.random() * Math.PI * 2,
          color: Math.random() > 0.75 ? colors.accentGlow : Math.random() > 0.5 ? '#6ea8ff' : '#ffffff',
        });
      }
      
      // Create fixed concentric circle points (like in the screenshot)
      concentricPoints = [
        { x: canvas.width * 0.5, y: canvas.height * 0.12, rings: 4, maxRadius: 45, color: '#6ea8ff', pulse: 0 },
        { x: canvas.width * 0.08, y: canvas.height * 0.32, rings: 1, maxRadius: 5, color: colors.accentGlow, pulse: 0 },
        { x: canvas.width * 0.12, y: canvas.height * 0.58, rings: 3, maxRadius: 28, color: colors.accentGlow, pulse: 0 },
        { x: canvas.width * 0.92, y: canvas.height * 0.30, rings: 2, maxRadius: 18, color: '#6ea8ff', pulse: 0 },
        { x: canvas.width * 0.88, y: canvas.height * 0.65, rings: 3, maxRadius: 25, color: '#6ea8ff', pulse: 0 },
      ];
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const drawConcentricCircles = (x, y, rings, maxRadius, color, pulseVal) => {
      for (let i = rings; i >= 1; i--) {
        const radius = (maxRadius / rings) * i;
        const animatedRadius = radius * (0.95 + Math.sin(pulseVal + i * 0.5) * 0.05);
        const opacity = 0.08 + (i / rings) * 0.12;
        
        ctx.beginPath();
        ctx.arc(x, y, animatedRadius, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.globalAlpha = opacity;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      
      // Center dot
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.7;
      ctx.fill();
    };
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const time = Date.now() * 0.001;
      
      // Draw flowing connection lines (curved)
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const other = nodes[j];
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 200 && distance > 40) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            
            // Curved lines for organic flow
            const midX = (node.x + other.x) / 2 + Math.sin(time * 0.5 + i * 0.1) * 15;
            const midY = (node.y + other.y) / 2 + Math.cos(time * 0.5 + j * 0.1) * 15;
            ctx.quadraticCurveTo(midX, midY, other.x, other.y);
            
            ctx.strokeStyle = '#ffffff';
            ctx.globalAlpha = (1 - distance / 200) * 0.08;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      
      // Update and draw nodes
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;
        
        // Wrap around edges smoothly
        if (node.x < -20) node.x = canvas.width + 20;
        if (node.x > canvas.width + 20) node.x = -20;
        if (node.y < -20) node.y = canvas.height + 20;
        if (node.y > canvas.height + 20) node.y = -20;
        
        node.pulsePhase += 0.012;
        const pulse = Math.sin(node.pulsePhase) * 0.35 + 0.65;
        
        if (node.type === 'concentric') {
          // Draw mini concentric circles for some nodes
          drawConcentricCircles(node.x, node.y, 2, 8, node.color, node.pulsePhase);
        } else if (node.type === 'large') {
          ctx.beginPath();
          ctx.arc(node.x, node.y, 4 * pulse, 0, Math.PI * 2);
          ctx.strokeStyle = node.color;
          ctx.globalAlpha = 0.2;
          ctx.lineWidth = 0.6;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(node.x, node.y, 2 * pulse, 0, Math.PI * 2);
          ctx.fillStyle = node.color;
          ctx.globalAlpha = 0.5;
          ctx.fill();
        } else if (node.type === 'medium') {
          ctx.beginPath();
          ctx.arc(node.x, node.y, 2.5 * pulse, 0, Math.PI * 2);
          ctx.fillStyle = node.color;
          ctx.globalAlpha = 0.35;
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
          ctx.fillStyle = node.color;
          ctx.globalAlpha = 0.25;
          ctx.fill();
        }
      });
      
      // Draw fixed concentric points (CASH, CLOSE, RISK, FORECAST areas)
      concentricPoints.forEach((cp) => {
        cp.pulse += 0.015;
        drawConcentricCircles(cp.x, cp.y, cp.rings, cp.maxRadius, cp.color, cp.pulse);
      });
      
      ctx.globalAlpha = 1;
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

// ============ DASHBOARD MOCKUP (exact match to screenshot) ============
const DashboardMockup = () => {
  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#0d1320' }}>
      {/* Browser chrome / title bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="h-3 w-3 rounded-full bg-red-500/80" />
            <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
            <div className="h-3 w-3 rounded-full bg-green-500/80" />
          </div>
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-4 py-1 rounded-md bg-white/5 text-[11px] text-white/40 font-mono">
            riicu.ai/dashboard
          </div>
        </div>
        <div className="text-[11px] text-white/40 font-mono">Q1 2026</div>
      </div>
      
      {/* Dashboard content */}
      <div className="p-4 md:p-6">
        {/* Sidebar + Main */}
        <div className="flex gap-4">
          {/* Sidebar */}
          <div className="hidden md:block w-44 shrink-0">
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm font-bold text-white">riicu</span>
              <span className="text-sm font-bold" style={{ color: colors.accentBrand }}>.ai</span>
            </div>
            <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/90" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Dashboard</span>
            </button>
          </div>
          
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Revenue</div>
                <div className="text-xl font-bold text-white">$142.8M</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                  <span>↗</span> +12.4% YoY
                </div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">EBITDA Margin</div>
                <div className="text-xl font-bold text-white">28.3%</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                  <span>↗</span> +2.1pp YoY
                </div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Close Cycle</div>
                <div className="text-xl font-bold text-white">4.2 days</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                  <span>↗</span> -38% YoY
                </div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Forecast Accuracy</div>
                <div className="text-xl font-bold text-white">94.7%</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                  <span>↗</span> +5.2pp YoY
                </div>
              </div>
            </div>
            
            {/* Charts row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 rounded-lg p-4" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-3">Revenue Trend</div>
                <div className="flex items-end justify-between h-28 gap-1.5">
                  {[45, 52, 48, 65, 58, 72, 68, 85, 78, 92, 88, 95].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t transition-all" style={{ height: `${h}%`, background: `linear-gradient(to top, rgba(212,168,83,0.7), rgba(212,168,83,0.2))` }} />
                  ))}
                </div>
              </div>
              <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-3">Budget vs Actual</div>
                <div className="relative h-28 flex items-center justify-center">
                  <div className="h-24 w-24 rounded-full" style={{ border: '4px solid rgba(212,168,83,0.3)' }} />
                  <div className="absolute h-24 w-24 rounded-full" style={{ border: '4px solid transparent', borderTopColor: colors.accentGlow, transform: 'rotate(-30deg)' }} />
                  <div className="absolute text-center">
                    <div className="text-lg font-bold text-white">87%</div>
                    <div className="text-[9px] text-white/40">on track</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ SCROLL REVEAL HOOK ============
const useScrollReveal = (threshold = 100) => {
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: `0px 0px -${threshold}px 0px` }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  
  return ref;
};

// ============ LOGIN MODAL ============
const LoginModal = ({ isOpen, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    const result = await login(username, password);
    if (!result.success) {
      if (result.error === 'network_error') {
        setError('Network error. Please try again.');
      } else {
        setError('Invalid username or password');
      }
    }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-8 md:p-10">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors">
          <X className="h-5 w-5" />
        </button>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="username" className="block text-sm font-medium text-slate-700">Username</label>
            <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your username" className="w-full px-4 py-3 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A0F1A]" data-testid="login-username" required />
          </div>
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">Password</label>
            <div className="relative">
              <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A0F1A]" data-testid="login-password" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error"><AlertCircle className="h-4 w-4" /><span>{error}</span></div>}
          <button type="submit" disabled={isSubmitting} className="w-full py-3 px-4 bg-[#0A0F1A] hover:bg-[#1a2332] text-white font-medium rounded-xl flex items-center justify-center gap-2 disabled:opacity-70" data-testid="login-submit">
            {isSubmitting ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><LogIn className="h-5 w-5" /><span>Login</span></>}
          </button>
          <div className="text-center">
            <button type="button" onClick={() => alert('Password recovery under development.')} className="text-sm text-slate-500 hover:text-[#0A0F1A] underline underline-offset-2">Forgot password</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ HERO SECTION ============
const HeroSection = ({ onClientAccess }) => {
  return (
    <section className="relative overflow-hidden pt-20 pb-10 md:pt-28 md:pb-14 min-h-screen flex flex-col justify-start" style={{ backgroundColor: colors.ink }}>
      <DataRiversBackground />
      
      {/* Soft radial focus overlay */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 55% 38% at 50% 38%, rgba(10,15,26,0.35) 0%, rgba(10,15,26,0.08) 55%, rgba(10,15,26,0) 80%)' }} />

      {/* Floating labels with concentric circles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
        {/* CASH - left upper */}
        <div className="absolute flex items-center gap-2" style={{ top: '28%', left: '6%' }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">Cash</span>
        </div>
        {/* CLOSE - left lower */}
        <div className="absolute flex items-center gap-2" style={{ bottom: '35%', left: '10%' }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">Close</span>
        </div>
        {/* RISK - right upper */}
        <div className="absolute flex items-center gap-2" style={{ top: '28%', right: '6%' }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">Risk</span>
        </div>
        {/* FORECAST - right lower */}
        <div className="absolute flex items-center gap-2" style={{ bottom: '32%', right: '8%' }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30">Forecast</span>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 z-10">
        <div className="mx-auto max-w-3xl text-center flex flex-col flex-1 min-h-[calc(100vh-9rem)]">
          {/* Tagline */}
          <p 
            className="mb-8 text-[12.4px] font-semibold uppercase tracking-[0.24em] animate-[fadeSlideUp_0.6s_ease-out_both]"
            style={{ color: colors.accentGlow }}
          >
            Cross-industry perspective on finance transformation
          </p>

          {/* Main heading */}
          <h1 className="text-[2.43rem] font-bold leading-[1.02] tracking-[-0.025em] text-white md:text-[3.06rem] lg:text-[4.65rem]" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
            <span className="inline-block animate-[fadeSlideUp_0.8s_ease-out_both]">Built for finance,</span>
            <br />
            <span
              className="inline-block font-normal italic mt-1 md:mt-2 text-[1.05em] animate-[fadeSlideUp_0.8s_ease-out_0.15s_both]"
              style={{
                fontFamily: 'Georgia, "Times New Roman", Times, serif',
                backgroundImage: 'linear-gradient(110deg, #e8c984 0%, #f5e2b1 35%, #b8c5ff 75%, #6ea8ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              by finance people.
            </span>
          </h1>

          {/* Description */}
          <p 
            className="mx-auto mt-3 max-w-xl text-[13.5px] leading-[1.7] text-white/70 md:text-[14.6px] lg:text-[15.75px] animate-[fadeSlideUp_0.7s_ease-out_0.3s_both]"
            style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
          >
            From fragmented finance processes to a clearer, more connected operating model — we help CFOs redesign workflows, validate the right solution, and move forward with confidence.
          </p>

          {/* CTA Buttons */}
          <div className="mt-auto mb-[76px] flex flex-col items-center gap-3 sm:flex-row sm:justify-center animate-[fadeSlideUp_0.6s_ease-out_0.45s_both]">
            <button
              onClick={onClientAccess}
              className="group relative inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold overflow-hidden transition-all hover:shadow-[0_20px_50px_-12px_rgba(212,168,83,0.4)] hover:-translate-y-0.5"
              style={{ color: colors.ink, boxShadow: 'inset 0 0 0 1px rgba(212,168,83,0.25), 0 1px 0 rgba(255,255,255,0.6) inset' }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Book a Diagnostic Session
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
            <button
              onClick={onClientAccess}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-medium text-white/80 backdrop-blur-md hover:text-white hover:border-white/30 hover:bg-white/[0.06] transition-all hover:-translate-y-0.5"
            >
              Explore Solutions
            </button>
          </div>
        </div>

        {/* Dashboard mockup */}
        <div className="mt-[22vh] md:mt-[26vh] animate-[fadeSlideUp_1s_ease-out_0.6s_both]">
          <div
            className="relative rounded-2xl p-[1px]"
            style={{ background: 'linear-gradient(135deg, rgba(212,168,83,0.35) 0%, rgba(255,255,255,0.06) 30%, rgba(110,168,255,0.18) 100%)' }}
          >
            <div className="rounded-2xl p-1 backdrop-blur-sm" style={{ backgroundColor: 'rgba(10,15,26,0.4)' }}>
              <DashboardMockup />
            </div>
          </div>
          <p className="mt-4 text-center text-[10.5px] font-mono uppercase tracking-[0.22em] text-white/35">
            Target-state environment
          </p>
        </div>
      </div>

      {/* Smooth fade into next section */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-32 md:h-40" style={{ background: `linear-gradient(to bottom, rgba(10,15,26,0) 0%, rgba(10,15,26,0.6) 55%, ${colors.surface} 100%)` }} />
    </section>
  );
};

// ============ TRANSFORM SECTION ============
const TransformSection = () => {
  const ref = useScrollReveal();
  
  return (
    <section ref={ref} className="py-20 md:py-28" style={{ backgroundColor: colors.surface }}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center reveal">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-4 block" style={{ color: colors.accentGlow }}>
            Finance Transformation
          </span>
          <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6" style={{ color: colors.ink }}>
            From Fragmented Processes to<br />
            <span className="italic" style={{ color: colors.accentGlow }}>Connected Operating Models</span>
          </h2>
          <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: 'rgba(10,15,26,0.6)' }}>
            We help CFO teams move beyond spreadsheets and siloed systems toward integrated workflows, 
            real-time visibility, and decision-ready reporting — designed around how your business actually operates.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============ EXECUTIVE HUB FEATURE ============
const hubBenefits = [
  { icon: Eye, title: "Visibility you can act on", desc: "Performance views, planning logic, reporting outputs and operational signals brought together so leadership can focus on what matters." },
  { icon: Workflow, title: "Workflows that hold", desc: "Approvals, close activities, reporting cycles and exceptions with clearer ownership, traceability and execution discipline." },
  { icon: Compass, title: "A direction, not a product", desc: "We help you define the target operating model, validate it through a live walkthrough, and shape a phased implementation path." },
];

const ExecutiveHubFeature = ({ onClientAccess }) => {
  const ref = useScrollReveal();

  return (
    <section className="relative py-20 md:py-28 overflow-hidden" ref={ref} style={{ backgroundColor: colors.ink }}>
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(ellipse at 15% 20%, rgba(37,99,235,0.10) 0%, transparent 55%), radial-gradient(ellipse at 90% 80%, rgba(212,168,83,0.06) 0%, transparent 50%)' }} />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="reveal">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-5 block" style={{ color: colors.accentGlow }}>
              Executive Management Hub
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.1] text-white" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              The kind of connected finance environment{" "}
              <span className="italic" style={{ color: colors.accentGlow }}>we help clients design over time.</span>
            </h2>
            <p className="mt-6 text-base md:text-[1.0625rem] leading-relaxed text-white/65 max-w-xl">
              Executive Management Hub is not a pre-built platform. It is a target-state operating environment we help finance leaders define, make tangible through functional demos, and implement in phases - with the right architecture and partners for their context.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={onClientAccess} className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.08em] hover:bg-white/90 transition-all" style={{ color: colors.ink }}>
                Explore the approach
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button onClick={onClientAccess} className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.08em] text-white hover:bg-white/5 transition-all">
                Book a diagnostic
              </button>
            </div>
          </div>

          <div className="grid gap-4 reveal">
            {hubBenefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20">
                <div className="flex items-start gap-4">
                  <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg shrink-0" style={{ backgroundColor: 'rgba(212,168,83,0.15)' }}>
                    <b.icon className="h-5 w-5" style={{ color: colors.accentGlow }} />
                  </div>
                  <div>
                    <h3 className="text-lg text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{b.title}</h3>
                    <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/30 px-1">
              Target-state architecture · phased delivery
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ ABOUT TEASER ============
const AboutTeaser = ({ onClientAccess }) => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.surface }}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-12 items-center md:grid-cols-2">
          <div className="reveal">
            <h2 className="text-3xl font-bold md:text-4xl mb-6" style={{ color: colors.ink }}>
              Turn Finance Pain Points<br />into Working Solutions.
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: 'rgba(10,15,26,0.6)' }}>
              riicu.ai was built on a simple conviction: finance transformation fails when technology is disconnected from process reality.
            </p>
            <p className="text-base leading-relaxed mb-6" style={{ color: 'rgba(10,15,26,0.6)' }}>
              We combine finance expertise, operating model design, and rapid prototyping to help CFO teams validate and implement solutions that fit how their business actually works.
            </p>
            <button onClick={onClientAccess} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors" style={{ backgroundColor: colors.accentBrand }}>
              Learn More <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Orbit visual */}
          <div className="relative flex items-center justify-center py-20 reveal">
            <div className="relative h-64 w-64">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-lg font-bold" style={{ color: colors.ink }}>riicu</span>
                  <span className="text-lg font-bold" style={{ color: colors.accentBrand }}>.ai</span>
                </div>
              </div>
              <div className="absolute inset-0 rounded-full" style={{ border: `1px solid rgba(10,15,26,0.1)` }} />
              <div className="absolute -inset-8 rounded-full" style={{ border: `1px solid rgba(10,15,26,0.05)` }} />
              <div className="absolute -inset-16 rounded-full" style={{ border: `1px solid rgba(10,15,26,0.03)` }} />
              <div className="absolute inset-0 animate-[spin_20s_linear_infinite]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 rounded-full" style={{ backgroundColor: colors.accentBrand }} />
              </div>
              <div className="absolute -inset-8 animate-[spin_25s_linear_infinite_reverse]">
                <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors.accentGlow }} />
              </div>
              <div className="absolute -inset-16 animate-[spin_35s_linear_infinite]">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 h-2 w-2 rounded-full" style={{ backgroundColor: colors.accentGlow }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ HOW WE WORK ============
const steps = [
  { num: "01", icon: Search, title: "Diagnose", desc: "We map your current processes, decision points, controls, and exceptions to identify the highest-value automation opportunities." },
  { num: "02", icon: Layers, title: "Demonstrate", desc: "We build a functional demo around your real workflow and decision points - so you can validate relevance, alignment, and adoption before committing." },
  { num: "03", icon: Rocket, title: "Implement", desc: "We deliver the target solution with the right architecture, controls, and partners - phased for measurable value at every stage." },
];

const HowWeWork = () => {
  const ref = useScrollReveal(120);

  return (
    <section ref={ref} className="relative py-16 md:py-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0c1222 0%, #131d36 50%, #0f1829 100%)', borderTop: `1px solid rgba(212,168,83,0.15)` }}>
      <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.08) 0%, transparent 60%)' }} />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center mb-14 reveal">
          <span className="text-xs font-semibold uppercase tracking-[0.08em] mb-4 block" style={{ color: colors.accentGlow }}>Our Approach</span>
          <h2 className="text-2xl font-bold text-white md:text-3xl lg:text-4xl tracking-tight">How We Work with CFO Teams</h2>
          <p className="mt-4 text-base text-white/50 leading-relaxed">From pain point to working solution - structured, transparent, and measurable at every step.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.num} className="reveal flex flex-col rounded-xl p-6 sm:p-8" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="mb-4 inline-flex items-center justify-center h-10 w-10 rounded-lg" style={{ backgroundColor: 'rgba(212,168,83,0.2)' }}>
                <s.icon className="h-5 w-5" style={{ color: colors.accentGlow }} />
              </div>
              <div className="text-xs font-bold font-mono mb-2 tracking-wider" style={{ color: 'rgba(212,168,83,0.6)' }}>STEP {s.num}</div>
              <h3 className="text-lg font-semibold text-white mb-3">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ INDUSTRIES SECTION ============
const industries = [
  { title: "Oil & Gas", image: "https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=640&q=80", accentRgb: "217, 119, 6" },
  { title: "Mining & Minerals", image: "https://images.unsplash.com/photo-1605289355680-75fb41239154?w=640&q=80", accentRgb: "212, 168, 83" },
  { title: "Agriculture", image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=640&q=80", accentRgb: "132, 160, 86" },
  { title: "Healthcare", image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=640&q=80", accentRgb: "14, 165, 233" },
  { title: "Pharmaceutical", image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=640&q=80", accentRgb: "139, 92, 246" },
  { title: "Financial Services", image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=640&q=80", accentRgb: "37, 99, 235" },
  { title: "Manufacturing", image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=640&q=80", accentRgb: "59, 130, 246" },
  { title: "Construction", image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=640&q=80", accentRgb: "234, 179, 8" },
  { title: "Real Estate", image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=640&q=80", accentRgb: "168, 125, 80" },
];

const IndustriesSection = ({ onClientAccess }) => {
  const ref = useScrollReveal();

  return (
    <section id="industries" className="py-20 md:py-28" ref={ref} style={{ backgroundColor: colors.surface }}>
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-2xl text-center mb-16 reveal">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] mb-3 block" style={{ color: colors.accentBrand }}>Industries</span>
          <h2 className="text-3xl font-bold md:text-4xl" style={{ color: colors.ink }}>Built for Complex, Capital-Intensive Sectors.</h2>
        </div>

        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3">
          {industries.map((ind) => (
            <button key={ind.title} onClick={onClientAccess} className="reveal group relative overflow-hidden rounded-lg h-40 lg:h-44 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.12)] text-left">
              <img src={ind.image} alt={ind.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 mix-blend-multiply opacity-40 transition-opacity duration-500 group-hover:opacity-60" style={{ background: `linear-gradient(135deg, rgba(${ind.accentRgb}, 0.55) 0%, rgba(${ind.accentRgb}, 0.15) 60%, transparent 100%)` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent group-hover:from-black/85 transition-colors duration-500" />
              <div className="absolute top-0 left-0 right-0 h-[3px] opacity-80 transition-opacity duration-300 group-hover:opacity-100" style={{ background: `rgb(${ind.accentRgb})` }} />
              <div className="relative flex h-full items-end p-4">
                <h3 className="text-[0.82rem] font-semibold text-white tracking-wide">{ind.title}</h3>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex justify-center reveal">
          <button onClick={onClientAccess} className="group inline-flex items-center gap-2 rounded-xl border bg-white px-6 py-3 text-sm font-medium hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all" style={{ borderColor: 'rgba(10,15,26,0.1)', color: colors.ink }}>
            View all 9 industries
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ============ WHY RIICU ============
const reasons = [
  { num: "01", title: "Designed Around Your Real Process", desc: "We map the current workflow, decision points, controls, and exceptions before proposing any solution. No generic templates forced onto your operations." },
  { num: "02", title: "Built for Measurable Value", desc: "We focus on reducing manual effort, accelerating cycle times, improving visibility, and strengthening auditability - with clear metrics from day one." },
  { num: "03", title: "Validated Before Full Implementation", desc: "We use functional demos and use-case prototypes to test relevance, alignment, and adoption before scaling - reducing risk and accelerating buy-in." },
];

const WhyRiicu = () => {
  const ref = useScrollReveal();

  return (
    <section className="py-20 md:py-28" ref={ref} style={{ backgroundColor: colors.ink }}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center mb-14 reveal">
          <h2 className="text-3xl font-bold text-white md:text-4xl">A More Controlled, More Efficient Finance Operating Model</h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {reasons.map((r) => (
            <div key={r.num} className="reveal rounded-xl border border-white/10 bg-white/5 p-6">
              <div className="text-3xl font-bold font-mono mb-3" style={{ color: 'rgba(37,99,235,0.4)' }}>{r.num}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{r.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ PRE-FOOTER ============
const PreFooter = ({ onClientAccess }) => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.surface }}>
      <div className="mx-auto max-w-4xl px-6 text-center">
        <h2 className="text-3xl font-bold md:text-4xl mb-6" style={{ color: colors.ink }}>
          Ready to Design Your<br />
          <span className="italic" style={{ color: colors.accentGlow }}>Connected Finance Environment?</span>
        </h2>
        <p className="text-base leading-relaxed mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(10,15,26,0.6)' }}>
          Book a diagnostic session to identify your highest-value opportunities for finance transformation.
        </p>
        <button onClick={onClientAccess} className="group inline-flex items-center gap-2 rounded-xl px-8 py-4 text-sm font-semibold text-white hover:opacity-90 transition-all" style={{ backgroundColor: colors.ink }}>
          Book a Diagnostic Session
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </section>
  );
};

// ============ HEADER ============
const Header = ({ onClientAccess }) => {
  return (
    <header className="fixed top-0 left-0 right-0 z-40 py-4 px-6 md:px-10" style={{ backgroundColor: 'rgba(10,15,26,0.85)', backdropFilter: 'blur(12px)' }}>
      <div className="mx-auto max-w-7xl flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-[22px] font-bold tracking-tight text-white">riicu</span>
          <span className="text-[22px] font-bold" style={{ color: colors.accentGlow }}>.</span>
          <span className="text-[22px] font-light text-white/60">ai</span>
        </div>
        <button onClick={onClientAccess} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/80 hover:text-white transition-all">
          <Lock className="h-4 w-4" />
          <span>Client Access</span>
        </button>
      </div>
    </header>
  );
};

// ============ MAIN PAGE ============
const LoginPage = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const handleClientAccess = () => setShowLoginModal(true);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.ink }}>
      <Header onClientAccess={handleClientAccess} />
      
      <div className={`transition-all duration-300 ${showLoginModal ? 'blur-[2px] opacity-80' : ''}`}>
        <HeroSection onClientAccess={handleClientAccess} />
        <TransformSection />
        <ExecutiveHubFeature onClientAccess={handleClientAccess} />
        <AboutTeaser onClientAccess={handleClientAccess} />
        <HowWeWork />
        <IndustriesSection onClientAccess={handleClientAccess} />
        <WhyRiicu />
        <PreFooter onClientAccess={handleClientAccess} />
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .reveal { opacity: 0; transform: translateY(20px); transition: opacity 0.6s ease-out, transform 0.6s ease-out; }
        .revealed { opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default LoginPage;
